import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { FiCalendar, FiUser, FiArrowLeft, FiShoppingBag } from 'react-icons/fi';
import { blogPosts } from '../data/blogPosts';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import WhatsAppShare from '../components/WhatsAppShare';

export default function BlogPost({ lang }) {
  const { slug } = useParams();
  const post = blogPosts.find((item) => item.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const t = {
    en: {
      backToBlog: 'Back to Blog',
      ctaTitle: 'Want the exact grocery list?',
      ctaButton: 'Download Free Guide',
      author: 'By'
    },
    es: {
      backToBlog: 'Volver al Blog',
      ctaTitle: '¿Quieres la lista de la compra exacta?',
      ctaButton: 'Descargar Guía Gratis',
      author: 'Por'
    }
  }[lang];

  const title = lang === 'en' ? post.titleEN : post.titleES;
  const content = lang === 'en' ? post.contentEN : post.contentES;
  const excerpt = lang === 'en' ? post.excerptEN : post.excerptES;
  const currentUrl = `https://barberadelvalles.fit/blog/${slug}${lang === 'en' ? '?lang=en' : ''}`;

  return (
    <div className="min-h-screen pb-20">
      <SEO
        title={`${title} | Barberà Fit`}
        description={excerpt}
        lang={lang}
        path={`/blog/${slug}`}
      />

      <article className="max-w-4xl mx-auto px-6 py-12">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-orange-500 font-bold uppercase tracking-widest text-xs mb-12 hover:text-white transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} />
          {t.backToBlog}
        </Link>

        <header className="mb-16 space-y-6">
          <div className="flex items-center gap-4 text-xs font-bold text-slate-500 uppercase tracking-widest">
            <span className="flex items-center gap-1 text-orange-500">
              <SafeIcon icon={FiCalendar} />
              {post.date}
            </span>
            <span className="text-slate-800">•</span>
            <span className="flex items-center gap-1">
              <SafeIcon icon={FiUser} />
              {t.author} {post.author}
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white leading-[0.9] tracking-tighter">
            {title}
          </h1>
          <div className="h-2 w-32 bg-orange-600" />
        </header>

        <div className="prose prose-invert prose-lg max-w-none prose-orange prose-headings:font-display prose-headings:uppercase prose-headings:tracking-tight prose-blockquote:border-orange-600 prose-blockquote:bg-slate-900/50 prose-blockquote:py-2 prose-blockquote:rounded-r-lg">
          <div dangerouslySetInnerHTML={{ __html: content }} />
          <WhatsAppShare url={currentUrl} lang={lang} />
        </div>

        <div className="mt-24 p-10 bg-slate-900 weathered-border border-orange-600/30 flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl">
          <div className="space-y-3 text-center md:text-left">
            <h2 className="text-3xl font-bold text-white uppercase italic tracking-tighter leading-none">
              {t.ctaTitle}
            </h2>
            <p className="text-slate-400 font-medium text-lg">
              Join 500+ Barberà residents burning fat with our local guide.
            </p>
          </div>

          <Link
            to="/"
            className="shrink-0 flex items-center gap-3 bg-orange-600 text-white px-10 py-5 font-bold uppercase tracking-widest hover:bg-white hover:text-orange-600 transition-all shadow-[0_0_20px_rgba(234,88,12,0.3)]"
          >
            <SafeIcon icon={FiShoppingBag} />
            {t.ctaButton}
          </Link>
        </div>
      </article>
    </div>
  );
}